/**
 * Service for Mollie payment integration
 * Handles subscriptions, payments, and billing
 */

import { getAuthHeaders } from './authService';
import { getTarifsByProfileType } from './tarifsService';

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/payments`;

// Cache for plans by profile type
let plansCache = {};
let plansCacheProfileTypeId = null;

// Mollie redirect URL (for callback after payment)
const MOLLIE_REDIRECT_URL = import.meta.env.VITE_MOLLIE_REDIRECT_URL;

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
    const responseText = await response.text();
    console.log('Payment API Response status:', response.status);

    let result;
    try {
        result = JSON.parse(responseText);
    } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }

    if (!response.ok) {
        const error = new Error(result.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = result;
        throw error;
    }

    return result;
};

/**
 * Create a new payment for a subscription
 * @param {string} peopleUuid - User's UUID
 * @param {string} planType - 'STARTER', 'PRO' or 'FULL'
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {Promise<Object>} Payment details with checkout URL
 */
export const createPayment = async (peopleUuid, planType, billingPeriod = 'monthly') => {
    try {
        console.log('Creating payment:', { peopleUuid, planType, billingPeriod });

        const response = await fetch(`${API_BASE_URL}/create-payment`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleUuid,
                planType,
                billingPeriod,
            })
        });

        const result = await handleResponse(response);
        console.log('Payment created:', result);
        return result;
    } catch (error) {
        console.error('Error creating payment:', error);
        throw error;
    }
};

/**
 * Get user's current subscription status
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Object>} Subscription details
 */
export const getSubscription = async (peopleUuid) => {
    try {
        console.log('Fetching subscription for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/subscription/${peopleUuid}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Subscription retrieved:', result);
        return result.data || result.DATA;
    } catch (error) {
        console.error('Error fetching subscription:', error);
        throw error;
    }
};

/**
 * Get user's payment history
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Array>} List of payments
 */
export const getPaymentHistory = async (peopleUuid) => {
    try {
        console.log('Fetching payment history for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/history/${peopleUuid}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Payment history retrieved:', result);
        return result.data || result.DATA || [];
    } catch (error) {
        console.error('Error fetching payment history:', error);
        throw error;
    }
};

/**
 * Get invoice data for a specific payment
 * @param {number} paymentId - Payment ID
 * @returns {Promise<Object>} Invoice data
 */
export const getInvoice = async (paymentId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/invoice/${paymentId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || null;
    } catch (error) {
        console.error('Error fetching invoice:', error);
        throw error;
    }
};

/**
 * Cancel user's subscription
 * @param {string} peopleUuid - User's UUID
 * @param {string} reason - Optional reason for cancellation
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelSubscription = async (peopleUuid, reason = '') => {
    try {
        console.log('Cancelling subscription for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/cancel-subscription`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleUuid,
                reason,
            })
        });

        const result = await handleResponse(response);
        console.log('Subscription cancelled:', result);
        return result;
    } catch (error) {
        console.error('Error cancelling subscription:', error);
        throw error;
    }
};

/**
 * Get available subscription tarifs from the database
 * @returns {Promise<Array>} List of tarifs
 */
export const getTarifs = async () => {
    try {
        console.log('Fetching tarifs from API');

        const response = await fetch(`${API_BASE_URL}/tarifs`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Tarifs retrieved:', result);
        return result.data || result.DATA || [];
    } catch (error) {
        console.error('Error fetching tarifs:', error);
        throw error;
    }
};

// Backward compatibility alias
export const getFormules = getTarifs;

/**
 * Get tarif change rules (bonus months when switching plans)
 * @param {string} fromTarifCode - Current tarif code (optional)
 * @param {string} toTarifCode - Target tarif code (optional)
 * @returns {Promise<Array>} List of change rules
 */
export const getTarifChangeRules = async (fromTarifCode = '', toTarifCode = '') => {
    try {
        let url = `${API_BASE_URL}/tarif-change-rules`;
        const params = new URLSearchParams();
        if (fromTarifCode) params.append('fromTarifCode', fromTarifCode);
        if (toTarifCode) params.append('toTarifCode', toTarifCode);
        if (params.toString()) url += `?${params.toString()}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || [];
    } catch (error) {
        console.error('Error fetching tarif change rules:', error);
        return [];
    }
};

/**
 * Get specific rule for a tarif change
 * @deprecated Use getChangeSimulation() instead
 * @param {string} fromTarifCode - Current tarif code
 * @param {string} toTarifCode - Target tarif code
 * @returns {Promise<Object|null>} Change rule or null
 */
export const getTarifChangeRule = async (fromTarifCode, toTarifCode) => {
    const rules = await getTarifChangeRules(fromTarifCode, toTarifCode);
    return rules.length > 0 ? rules[0] : null;
};

/**
 * Simulate a plan change (upgrade or downgrade) with prorata calculation
 * @param {string} peopleUuid - User's UUID
 * @param {string} planType - Target plan code (e.g., 'FULL', 'STARTER')
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {Promise<Object>} Simulation result with isUpgrade, isDowngrade, bonusDays, amountToPay, etc.
 */
export const getChangeSimulation = async (peopleUuid, planType, billingPeriod = 'monthly') => {
    try {
        const params = new URLSearchParams({ peopleUuid, planType, billingPeriod });
        const response = await fetch(`${API_BASE_URL}/calculate-change?${params.toString()}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || null;
    } catch (error) {
        console.error('Error fetching change simulation:', error);
        return null;
    }
};

/**
 * Upgrade subscription to a higher plan with prorata credit
 * @param {string} peopleUuid - User's UUID
 * @param {string} newPlanType - New plan type (e.g., 'FULL')
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {Promise<Object>} Upgrade result with payment URL or immediate activation
 */
export const upgradeSubscription = async (peopleUuid, newPlanType, billingPeriod = 'monthly') => {
    try {
        console.log('Upgrading subscription:', { peopleUuid, newPlanType, billingPeriod });

        const response = await fetch(`${API_BASE_URL}/upgrade-subscription`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleUuid,
                newPlanType,
                billingPeriod,
            })
        });

        const result = await handleResponse(response);
        console.log('Upgrade result:', result);
        return result;
    } catch (error) {
        console.error('Error upgrading subscription:', error);
        throw error;
    }
};

/**
 * Get available credits for a user
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Object>} Credits info with total and list
 */
export const getCredits = async (peopleUuid) => {
    try {
        console.log('Fetching credits for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/credits/${peopleUuid}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('Credits retrieved:', result);
        return {
            credits: result.data || result.DATA || [],
            totalCredit: result.totalCredit || result.TOTALCREDIT || 0
        };
    } catch (error) {
        console.error('Error fetching credits:', error);
        throw error;
    }
};

/**
 * Get subscription statistics (admin only)
 * @returns {Promise<Object>} Stats by plan
 */
export const getSubscriptionStats = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/subscription-stats`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || {};
    } catch (error) {
        console.error('Error fetching subscription stats:', error);
        throw error;
    }
};

/**
 * Normalize tarif keys from API (handles uppercase/lowercase)
 * @param {Object} tarif - Tarif from API
 * @returns {Object} Normalized tarif
 */
const normalizeTarifKeys = (tarif) => {
    return {
        id: tarif.id ?? tarif.ID,
        code: (tarif.code ?? tarif.CODE ?? '').toUpperCase(),
        name: tarif.name_fr ?? tarif.NAME_FR ?? tarif.name ?? tarif.NAME ?? '',
        name_fr: tarif.name_fr ?? tarif.NAME_FR ?? '',
        name_en: tarif.name_en ?? tarif.NAME_EN ?? '',
        name_nl: tarif.name_nl ?? tarif.NAME_NL ?? '',
        name_de: tarif.name_de ?? tarif.NAME_DE ?? '',
        name_es: tarif.name_es ?? tarif.NAME_ES ?? '',
        name_it: tarif.name_it ?? tarif.NAME_IT ?? '',
        name_pt: tarif.name_pt ?? tarif.NAME_PT ?? '',
        name_el: tarif.name_el ?? tarif.NAME_EL ?? '',
        description: tarif.description_fr ?? tarif.DESCRIPTION_FR ?? tarif.description ?? tarif.DESCRIPTION ?? '',
        description_fr: tarif.description_fr ?? tarif.DESCRIPTION_FR ?? '',
        description_en: tarif.description_en ?? tarif.DESCRIPTION_EN ?? '',
        description_nl: tarif.description_nl ?? tarif.DESCRIPTION_NL ?? '',
        description_de: tarif.description_de ?? tarif.DESCRIPTION_DE ?? '',
        description_es: tarif.description_es ?? tarif.DESCRIPTION_ES ?? '',
        description_it: tarif.description_it ?? tarif.DESCRIPTION_IT ?? '',
        description_pt: tarif.description_pt ?? tarif.DESCRIPTION_PT ?? '',
        description_el: tarif.description_el ?? tarif.DESCRIPTION_EL ?? '',
        price: parseFloat(tarif.finalmonthlyprice ?? tarif.FINALMONTHLYPRICE ?? tarif.monthlyprice ?? tarif.MONTHLYPRICE ?? 0),
        priceYearly: parseFloat(tarif.finalyearlyprice ?? tarif.FINALYEARLYPRICE ?? tarif.yearlyprice ?? tarif.YEARLYPRICE ?? 0),
        popular: tarif.ispopular ?? tarif.ISPOPULAR ?? tarif.isPopular ?? false,
        sortorder: tarif.sortorder ?? tarif.SORTORDER ?? 0,
        _original: tarif
    };
};

/**
 * Get plans for a specific profile type from the database
 * Uses cache to avoid repeated API calls
 * @param {number} profileTypeId - Profile type ID (optional, uses localStorage if not provided)
 * @returns {Promise<Object>} Plans object keyed by code
 */
export const getPlansForProfileType = async (profileTypeId = null) => {
    // Get profile type ID from param or localStorage
    const ptId = profileTypeId || parseInt(localStorage.getItem('userProfileTypeId'), 10);

    if (!ptId) {
        console.warn('No profile type ID available, returning default FREE plan');
        return { FREE: DEFAULT_FREE_PLAN };
    }

    // Return cached plans if same profile type
    if (plansCacheProfileTypeId === ptId && Object.keys(plansCache).length > 0) {
        return plansCache;
    }

    try {
        const tarifs = await getTarifsByProfileType(ptId);
        const plans = {};

        tarifs.forEach(tarif => {
            const normalized = normalizeTarifKeys(tarif);
            plans[normalized.code] = normalized;
        });

        // Always ensure FREE plan exists
        if (!plans.FREE) {
            plans.FREE = DEFAULT_FREE_PLAN;
        }

        // Cache the result
        plansCache = plans;
        plansCacheProfileTypeId = ptId;

        console.log(`[paymentService] Loaded ${Object.keys(plans).length} plans for profile type ${ptId}`);
        return plans;
    } catch (error) {
        console.error('Error fetching plans for profile type:', error);
        return { FREE: DEFAULT_FREE_PLAN };
    }
};

/**
 * Clear the plans cache (call when user changes or logs out)
 */
export const clearPlansCache = () => {
    plansCache = {};
    plansCacheProfileTypeId = null;
};

/**
 * Default FREE plan (fallback)
 */
const DEFAULT_FREE_PLAN = {
    id: 0,
    code: 'FREE',
    name: 'Free',
    name_fr: 'Gratuit',
    name_en: 'Free',
    name_nl: 'Gratis',
    name_de: 'Kostenlos',
    name_es: 'Gratuito',
    name_it: 'Gratuito',
    name_pt: 'Gratuito',
    name_el: 'Δωρεάν',
    description: '',
    description_fr: '',
    description_en: '',
    description_nl: '',
    description_de: '',
    description_es: '',
    description_it: '',
    description_pt: '',
    description_el: '',
    price: 0,
    priceYearly: 0,
    popular: false,
    sortorder: 0
};

/**
 * DEPRECATED: Use getPlansForProfileType() instead
 * This is kept for backward compatibility but will return empty
 * @deprecated
 */
export const PLANS = {};

/**
 * Get price for a plan based on billing period
 * @param {Object} plan - Plan object (from getPlansForProfileType)
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @returns {number} Price
 */
export const getPlanPrice = (plan, billingPeriod = 'monthly') => {
    if (!plan) return 0;
    return billingPeriod === 'yearly' ? (plan.priceYearly || 0) : (plan.price || 0);
};

/**
 * Get savings percentage for yearly billing
 * @param {Object} plan - Plan object (from getPlansForProfileType)
 * @returns {number} Savings percentage
 */
export const getYearlySavings = (plan) => {
    if (!plan || !plan.price || plan.price === 0) return 0;
    const monthlyTotal = plan.price * 12;
    const savings = ((monthlyTotal - (plan.priceYearly || 0)) / monthlyTotal) * 100;
    return Math.round(savings);
};

/**
 * Get the next upgrade plan from a plans object
 * @param {string} currentPlanCode - Current plan code
 * @param {Object} plans - Plans object from getPlansForProfileType
 * @returns {Object|null} Next plan or null if already at max
 */
export const getUpgradePlan = (currentPlanCode, plans) => {
    if (!plans || !currentPlanCode) return null;

    const plansArray = Object.values(plans).sort((a, b) => (a.price || 0) - (b.price || 0));
    const currentIndex = plansArray.findIndex(p => p.code === currentPlanCode.toUpperCase());

    if (currentIndex === -1 || currentIndex >= plansArray.length - 1) {
        return null;
    }

    return plansArray[currentIndex + 1];
};
